import { useState, useMemo, useCallback } from 'react';
import { Popover } from '@mui/material';

import { DocumentPopupDropdownProps, DropdownContextType } from './types';
import useLongDateTime from 'app/hooks/useLongDateTime';

import DropdownContext from './DropdownContext';

import { DropdownTrigger } from './DropdownTrigger';
import { DropdownFilters } from './DropdownFilters';
import { DropdownList } from './DropdownList';

export const DocumentPopupDropdown = <T extends string | number>({
  value,
  options,
  onChange,
  placeholder = 'Select documents...',
  maxChips = 3,
}: DocumentPopupDropdownProps<T>) => {
  const [, formatDateTime] = useLongDateTime({
    displayTime: true,
    local: true
  })
  
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastClickedId, setLastClickedId] = useState<T | null>(null);

  const open = Boolean(anchorEl);
  const id = open ? 'document-popup-dropdown' : undefined;

  // --- Filtering Logic (Moved to Parent) ---
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const lowerTerm = searchTerm.toLowerCase();
    return options.filter((option) => {
      const doc = option.props?.document;
      if (!doc) return false;
      // 1. Search by Document Name
      if (doc.name.toLowerCase().includes(lowerTerm)) return true;
      const createdOn = formatDateTime(doc.createdOn);
      // 2. Search by createdAt (Date parts: year, day, partial month name)
      if (createdOn && createdOn.toLowerCase().includes(lowerTerm)) return true;
      // 3. Search by Labels
      if (doc.labels && doc.labels.some(label => label.name.toLowerCase().includes(lowerTerm))) return true;
      return false;
    });
  }, [options, searchTerm, formatDateTime]);

  // --- Selection Handlers ---
  // 💡 MODIFIED: handleToggle now accepts the click event to check for Shift key
  const handleToggle = useCallback((optionId: T, event?: React.MouseEvent) => {
    const isShiftClick = event?.shiftKey;
    const isCurrentlySelected = value.includes(optionId);
    let newChecked = [...value];

    if (isShiftClick && lastClickedId !== null) {
      const allVisibleIds = filteredOptions.map(opt => opt.id);
      
      const currentIndex = allVisibleIds.indexOf(optionId);
      const lastIndex = allVisibleIds.indexOf(lastClickedId);

      const start = Math.min(currentIndex, lastIndex);
      const end = Math.max(currentIndex, lastIndex);
      
      const idsInRange = allVisibleIds.slice(start, end + 1);

      // Determine the action based on the state of the *target* item (optionId)
      // If the target item is already selected, Shift-click means deselect the range.
      // If the target item is NOT selected, Shift-click means select the range.
      if (isCurrentlySelected) {
        // Deselect the range
        newChecked = newChecked.filter(id => !idsInRange.includes(id));
      } else {
        // Select the range
        const toAdd = idsInRange.filter(id => !newChecked.includes(id));
        newChecked = [...newChecked, ...toAdd];
      }
    } else {
      // Standard click (toggle single item)
      if (isCurrentlySelected) {
        newChecked = newChecked.filter(id => id !== optionId);
      } else {
        newChecked.push(optionId);
      }
    }

    onChange(newChecked);
    // 💡 IMPORTANT: Update lastClickedId only if it's not a Shift-click 
    // to anchor the next range selection.
    if (!isShiftClick) setLastClickedId(optionId);
  }, [value, onChange, lastClickedId, filteredOptions]);

  const handleDeleteChip = useCallback((e: React.MouseEvent, idToDelete: T) => {
    e.stopPropagation(); 
    onChange(value.filter((id) => id !== idToDelete));
  }, [value, onChange]);

  const handleClearAll = useCallback(() => {
    const filteredIds = filteredOptions.map(opt => opt.id);
    const newSelectedIds = value.filter(id => !filteredIds.includes(id));
    
    onChange(newSelectedIds);
  }, [value, filteredOptions, onChange]);

  const handleSelectAll = useCallback(() => {
    // Only performs selection
    const filteredIds = filteredOptions.map(opt => opt.id);

    const toAdd = filteredIds.filter(id => !value.includes(id));
    const newSelectedIds = [...value, ...toAdd];

    onChange(newSelectedIds);

    if (filteredIds.length > 0) setLastClickedId(filteredIds[filteredIds.length - 1]);
  }, [value, filteredOptions, onChange]);

  const contextValue: DropdownContextType<T> = useMemo(() => ({
    value,
    options,
    onChange,
    handleToggle,
    searchTerm,
    setSearchTerm,
    filteredOptions,
    handleSelectAll,
    handleClearAll
  }), [value, options, onChange, handleToggle, searchTerm, setSearchTerm, filteredOptions, handleSelectAll, handleClearAll]);

  return (
    <DropdownContext.Provider value={contextValue as DropdownContextType<any>}>
      <DropdownTrigger 
        open={open}
        placeholder={placeholder}
        maxChips={maxChips}
        handleClick={(e) => setAnchorEl(e.currentTarget)}
        handleDeleteChip={handleDeleteChip}
      />
      {anchorEl ? (
        <Popover
          id={id}
          open={true}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: { width: anchorEl.clientWidth || 300 }
            }
          }}
        >
          <DropdownFilters />
          <DropdownList />
        </Popover>
      ) : null}
    </DropdownContext.Provider>
  );
};